/**
 * Build Your Bundle Mobile Component
 * Handles mobile drawer-based bundle building
 */
class BuildYourBundleMobile extends HTMLElement {
  connectedCallback() {
    this.sectionId = this.dataset.sectionId;
    this.inStockOption = this.dataset.inStockOption || '';
    this.moneyFormat = this.dataset.moneyFormat || '{{amount}}';
    this.savedAmountLabel = this.dataset.savedAmountLabel || 'Saved amount';
    this.addToCartText = this.dataset.addToCartText || 'Add bundle to cart';
    this.discount2Items = parseInt(
      this.dataset.discount2Items || this.getAttribute('data-discount-2-items') || '10',
      10
    );
    this.discount3Items = parseInt(
      this.dataset.discount3Items || this.getAttribute('data-discount-3-items') || '20',
      10
    );

    // Translation strings (with hardcoded fallbacks in JS)
    this.addingText = this.dataset.addingText || 'Adding...';
    this.removeProductLabel = this.dataset.removeProductLabel || 'Remove product';
    this.errorUnavailableText =
      this.dataset.errorUnavailableText || 'Some products are no longer available. Please update your selection.';
    this.errorAddToCartText = this.dataset.errorAddToCartText || 'Failed to add items to cart';
    this.errorTimeoutText = this.dataset.errorTimeoutText || 'Request timed out. Please try again.';
    this.errorRetryText = this.dataset.errorRetryText || 'Failed to add items to cart. Please try again.';

    // Fix #3: Race condition prevention
    this.isUpdating = false;

    // Get shared bundle state
    this.bundleState = this.getBundleState();

    // Current slot being edited
    this.currentSlotIndex = null;

    // Get DOM elements
    this.slots = this.querySelectorAll('.build-your-bundle__slot');
    this.drawer = this.querySelector('.build-your-bundle__drawer');
    this.drawerProducts = this.querySelector('[data-drawer-products]');
    this.form = this.querySelector('[data-bundle-form]');
    this.addToCartButton = this.querySelector('[data-add-to-cart-button]');
    this.savedAmountDisplay = this.querySelector('[data-saved-amount-display]');

    // Initialize
    this.setupEventListeners();
    this.listenToBundleChanges();
    this.updateUI();
  }

  getBundleState() {
    // Fix #1: Use Set for listeners to prevent duplicates
    const stateId = `bundle-state-${this.sectionId}`;
    if (!window[stateId]) {
      window[stateId] = {
        bundleSlots: [null, null, null],
        listeners: new Set(), // Use Set to prevent duplicates
      };
    }
    return window[stateId];
  }

  listenToBundleChanges() {
    // Listen for bundle state changes from other components
    this.addEventListener('bundle:state-changed', () => {
      this.updateUI();
    });
  }

  notifyBundleChange() {
    // Fix #3: Use queueMicrotask for async notification to prevent race conditions
    queueMicrotask(() => {
      this.bundleState.listeners.forEach((listener) => {
        if (listener !== this) {
          listener.dispatchEvent(new CustomEvent('bundle:state-changed'));
        }
      });
    });
  }

  setupEventListeners() {
    // Use event delegation for slot clicks to handle innerHTML replacements
    const slotsContainer = this.querySelector('.build-your-bundle__slots');
    if (slotsContainer) {
      slotsContainer.addEventListener('click', (e) => {
        // Find the clicked slot
        const slot = e.target.closest('.build-your-bundle__slot');
        if (!slot) return;

        // Handle remove button clicks
        if (e.target.closest('.build-your-bundle__slot-remove')) {
          e.stopPropagation();
          const slotIndex = parseInt(slot.dataset.slotIndex, 10);
          this.removeSlot(slotIndex);
          return;
        }

        // Don't open drawer if slot is disabled
        if (slot.classList.contains('disabled')) {
          return;
        }

        // Open drawer for slot
        const slotIndex = parseInt(slot.dataset.slotIndex, 10);
        this.openDrawer(slotIndex);
      });
    }

    // Handle product selection in drawer
    if (this.drawerProducts) {
      this.drawerProducts.addEventListener('click', (e) => {
        const productElement = e.target.closest('.build-your-bundle__drawer-product');
        if (productElement && !productElement.classList.contains('sold-out')) {
          this.selectProduct(productElement);
        }
      });
    }

    // Handle form submission
    if (this.form) {
      this.form.addEventListener('submit', (e) => {
        e.preventDefault();
        this.addToCart();
      });
    }

    // Handle drawer close events
    if (this.drawer) {
      this.drawer.addEventListener('dialog:force-close', () => {
        this.currentSlotIndex = null;
      });
    }

    // Fix #1: Register this component as a listener (Set prevents duplicates)
    this.bundleState.listeners.add(this);
  }

  openDrawer(slotIndex) {
    this.currentSlotIndex = slotIndex;
    if (this.drawer) {
      this.drawer.show();
    }
  }

  selectProduct(productElement) {
    if (this.currentSlotIndex === null) return;

    const productId = productElement.dataset.productId;
    const variantId = productElement.dataset.variantId;
    const productTitle = productElement.dataset.productTitle;
    const productPrice = parseInt(productElement.dataset.productPrice, 10);
    const productComparePrice = parseInt(productElement.dataset.productComparePrice || '0', 10);
    const productImage = productElement.dataset.productImage;
    const variantAvailable = productElement.dataset.variantAvailable === 'true';

    if (!variantAvailable) {
      return;
    }

    // Store selected product in slot
    this.bundleState.bundleSlots[this.currentSlotIndex] = {
      productId,
      variantId,
      productTitle,
      productPrice,
      productComparePrice,
      productImage,
    };

    // Close drawer
    if (this.drawer) {
      this.drawer.hide();
    }

    // Update UI and notify other components
    this.updateUI();
    this.notifyBundleChange();
    this.currentSlotIndex = null;
  }

  removeSlot(slotIndex) {
    // Remove the slot and shift items forward
    this.bundleState.bundleSlots[slotIndex] = null;

    // Shift items forward (compact the array)
    for (let i = slotIndex; i < this.bundleState.bundleSlots.length - 1; i++) {
      if (this.bundleState.bundleSlots[i + 1] != null) {
        this.bundleState.bundleSlots[i] = this.bundleState.bundleSlots[i + 1];
        this.bundleState.bundleSlots[i + 1] = null;
      }
    }

    this.updateUI();
    this.notifyBundleChange();
  }

  updateUI() {
    const bundleSlots = this.bundleState.bundleSlots;

    // Determine which slots should be enabled (sequential logic)
    const enabledSlots = [];
    for (let i = 0; i < bundleSlots.length; i++) {
      if (i === 0) {
        enabledSlots[i] = true;
      } else {
        enabledSlots[i] = bundleSlots[i - 1] != null;
      }
    }

    // Update each slot's appearance
    this.slots.forEach((slot) => {
      const slotIndex = parseInt(slot.dataset.slotIndex, 10);
      const slotData = bundleSlots[slotIndex];
      const discountPercentage = slot.dataset.discountPercentage || '0';
      const isEnabled = enabledSlots[slotIndex];

      // Update disabled state
      if (isEnabled) {
        slot.classList.remove('disabled');
      } else {
        slot.classList.add('disabled');
      }

      if (slotData == null) {
        // Empty slot
        slot.classList.remove('build-your-bundle__slot--filled');
        slot.classList.add('build-your-bundle__slot--empty');
        const discountBadge =
          parseInt(discountPercentage, 10) > 0
            ? `<div class="build-your-bundle__slot-discount">
              <svg class="build-your-bundle__slot-discount-icon" width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M13 4L6 11L3 8" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
              ${discountPercentage}% OFF
            </div>`
            : '';
        slot.innerHTML = `
          <div class="build-your-bundle__slot-add-area">
            <svg class="build-your-bundle__slot-add-icon" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <line x1="12" y1="5" x2="12" y2="19"></line>
              <line x1="5" y1="12" x2="19" y2="12"></line>
            </svg>
          </div>
          <div class="build-your-bundle__slot-text">
            ${slot.dataset.emptyText || 'Add to get extra discount'}
          </div>
          ${discountBadge}
        `;
      } else {
        // Filled slot
        slot.classList.remove('build-your-bundle__slot--empty');
        slot.classList.add('build-your-bundle__slot--filled');

        const comparePrice =
          slotData.productComparePrice && slotData.productComparePrice > slotData.productPrice
            ? `<span class="build-your-bundle__slot-compare-price">${this.formatMoney(
                slotData.productComparePrice
              )}</span>`
            : '';

        slot.innerHTML = `
          <div class="build-your-bundle__slot-content">
            <img src="${slotData.productImage}" alt="${
          slotData.productTitle
        }" class="build-your-bundle__slot-image" loading="lazy">
            <div class="build-your-bundle__slot-info">
              <div class="build-your-bundle__slot-title">${slotData.productTitle}</div>
              <div class="build-your-bundle__slot-price-wrapper">
                <span class="build-your-bundle__slot-price">${this.formatMoney(slotData.productPrice)}</span>
                ${comparePrice}
              </div>
            </div>
          </div>
          <button type="button" class="build-your-bundle__slot-remove" aria-label="${this.removeProductLabel}">
            <svg class="build-your-bundle__slot-remove-icon" width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="2">
              <line x1="5" y1="5" x2="15" y2="15"></line>
              <line x1="15" y1="5" x2="5" y2="15"></line>
            </svg>
          </button>
        `;
      }
    });

    // Update saved amount display
    this.updateSavedAmount();

    // Update add to cart button state and price
    this.updateAddToCartButton();
  }

  updateSavedAmount() {
    const bundleSlots = this.bundleState.bundleSlots;
    const hasItems = bundleSlots.some((slot) => slot != null);

    if (!this.savedAmountDisplay) return;

    if (hasItems) {
      const { totalOriginal, totalDiscounted, totalSavings } = this.calculateSavings();

      if (totalSavings > 0) {
        const formattedSavings = this.formatMoney(totalSavings);
        this.savedAmountDisplay.setAttribute('data-has-items', 'true');

        const savingsValue = this.savedAmountDisplay.querySelector('[data-saved-amount-value]');
        if (savingsValue) {
          savingsValue.textContent = formattedSavings;
        }

        this.savedAmountDisplay.style.display = 'flex';
      } else {
        this.savedAmountDisplay.setAttribute('data-has-items', 'false');
        this.savedAmountDisplay.style.display = 'none';
      }
    } else {
      this.savedAmountDisplay.setAttribute('data-has-items', 'false');
      this.savedAmountDisplay.style.display = 'none';
    }
  }

  calculateSavings() {
    const bundleSlots = this.bundleState.bundleSlots;
    let totalOriginal = 0;
    let itemCount = 0;

    // Calculate total original price and count items
    bundleSlots.forEach((slotData) => {
      if (slotData != null) {
        totalOriginal += slotData.productPrice;
        itemCount++;
      }
    });

    // Determine bundle discount based on item count (from customizer settings)
    let bundleDiscountPercentage = 0;
    if (itemCount === 2) {
      bundleDiscountPercentage = this.discount2Items;
    } else if (itemCount === 3) {
      bundleDiscountPercentage = this.discount3Items;
    }

    // Apply discount to total bundle price
    const totalDiscounted = Math.round(totalOriginal * (1 - bundleDiscountPercentage / 100));
    const totalSavings = totalOriginal - totalDiscounted;

    return {
      totalOriginal,
      totalDiscounted,
      totalSavings,
    };
  }

  updateAddToCartButton() {
    const bundleSlots = this.bundleState.bundleSlots;
    const hasProducts = bundleSlots.some((slot) => slot != null);

    if (!this.addToCartButton) return;

    this.addToCartButton.disabled = !hasProducts;
    if (!hasProducts) {
      this.addToCartButton.setAttribute('disabled', 'disabled');
      const priceDisplay = this.addToCartButton.querySelector('[data-add-to-cart-price]');
      if (priceDisplay) {
        priceDisplay.style.display = 'none';
      }
    } else {
      this.addToCartButton.removeAttribute('disabled');

      const { totalOriginal, totalDiscounted } = this.calculateSavings();
      const priceDisplay = this.addToCartButton.querySelector('[data-add-to-cart-price]');
      const originalPriceEl = this.addToCartButton.querySelector('[data-price-original]');
      const finalPriceEl = this.addToCartButton.querySelector('[data-price-final]');

      if (priceDisplay && originalPriceEl && finalPriceEl) {
        priceDisplay.style.display = 'flex';
        originalPriceEl.textContent = this.formatMoney(totalOriginal);
        finalPriceEl.textContent = this.formatMoney(totalDiscounted);
      }
    }
  }

  async addToCart(retryCount = 0) {
    const bundleSlots = this.bundleState.bundleSlots;
    const selectedVariants = bundleSlots.filter((slot) => slot != null);

    if (selectedVariants.length === 0) {
      return;
    }

    // Fix #2: Validate product availability before submission
    const unavailableProducts = selectedVariants.filter((slot) => !slot.variantId);
    if (unavailableProducts.length > 0) {
      this.showError(this.errorUnavailableText);
      return;
    }

    // Disable button during submission
    if (this.addToCartButton) {
      this.addToCartButton.disabled = true;
      this.addToCartButton.setAttribute('aria-busy', 'true');
      const buttonText = this.addToCartButton.querySelector('span');
      if (buttonText) {
        buttonText.textContent = this.addingText;
      }
    }

    // Prepare sections to bundle
    const sectionsToBundle = ['variant-added', 'cart-drawer'];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:prepare-bundled-sections', {
        bubbles: true,
        detail: { sections: sectionsToBundle },
      })
    );

    // Prepare form data
    const formData = new FormData();
    selectedVariants.forEach((slot) => {
      formData.append('id[]', slot.variantId);
    });
    formData.append('quantity', '1');
    formData.set('sections', sectionsToBundle.join(','));

    if (selectedVariants.length > 0) {
      formData.set('sections_url', `${Shopify.routes.root}variants/${selectedVariants[0].variantId}`);
    }

    try {
      // Fix #2: Add timeout handling (10 seconds)
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000);

      const response = await fetch(`${Shopify.routes.root}cart/add.js`, {
        method: 'POST',
        body: formData,
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
        },
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      const responseJson = await response.json();

      if (response.ok) {
        const cartResponse = await fetch(`${Shopify.routes.root}cart.js`);
        const cart = await cartResponse.json();

        if (responseJson.sections) {
          cart.sections = responseJson.sections;
        }

        this.dispatchEvent(
          new CustomEvent('variant:add', {
            bubbles: true,
            detail: {
              items: responseJson.hasOwnProperty('items') ? responseJson.items : [responseJson],
              cart: cart,
            },
          })
        );

        document.documentElement.dispatchEvent(
          new CustomEvent('cart:change', {
            bubbles: true,
            detail: {
              baseEvent: 'variant:add',
              cart: cart,
            },
          })
        );

        const cartDrawer = document.querySelector('cart-drawer');
        if (cartDrawer && window.themeVariables?.settings?.cartType !== 'page') {
          cartDrawer.show();
        }

        // Reset bundle
        this.bundleState.bundleSlots = [null, null, null];
        this.updateUI();
        this.notifyBundleChange();
      } else {
        // Fix #2: Handle specific error cases
        const errorMessage = responseJson.description || this.errorAddToCartText;

        // Dispatch cart:error event for theme-level error handling
        document.documentElement.dispatchEvent(
          new CustomEvent('cart:error', {
            bubbles: true,
            detail: {
              error: errorMessage,
              response: responseJson,
            },
          })
        );

        this.showError(errorMessage);
        console.error('[BuildYourBundleMobile] Cart error:', responseJson);
      }
    } catch (error) {
      console.error('[BuildYourBundleMobile] Network error:', error);

      // Fix #2: Implement retry logic with exponential backoff
      if (error.name === 'AbortError') {
        this.showError(this.errorTimeoutText);
      } else if (retryCount < 2) {
        // Retry with exponential backoff
        const delay = Math.pow(2, retryCount) * 1000;
        setTimeout(() => {
          this.addToCart(retryCount + 1);
        }, delay);
        return; // Don't re-enable button yet
      } else {
        // Dispatch cart:error event
        document.documentElement.dispatchEvent(
          new CustomEvent('cart:error', {
            bubbles: true,
            detail: {
              error: error.message,
              originalError: error,
            },
          })
        );

        this.showError(this.errorRetryText);
      }
    } finally {
      if (this.addToCartButton) {
        this.addToCartButton.disabled = false;
        this.addToCartButton.removeAttribute('aria-busy');
        const buttonText = this.addToCartButton.querySelector('span');
        if (buttonText) {
          buttonText.textContent = this.addToCartText;
        }
      }
    }
  }

  // Fix #2: Add proper error display method
  showError(message) {
    // Try to find or create an error display element
    let errorDisplay = this.querySelector('[data-error-display]');

    if (!errorDisplay) {
      errorDisplay = document.createElement('div');
      errorDisplay.setAttribute('data-error-display', '');
      errorDisplay.setAttribute('role', 'alert');
      errorDisplay.style.cssText =
        'color: #d00; padding: 10px; margin: 10px 0; border: 1px solid #d00; border-radius: 4px;';
      this.insertBefore(errorDisplay, this.firstChild);
    }

    errorDisplay.textContent = message;
    errorDisplay.style.display = 'block';

    // Auto-hide after 5 seconds
    setTimeout(() => {
      if (errorDisplay) {
        errorDisplay.style.display = 'none';
      }
    }, 5000);
  }

  formatMoney(cents) {
    if (typeof cents === 'string') {
      cents = parseInt(cents, 10);
    }

    // "Hide trailing zeros" theme setting: whole amounts drop their decimals ($199 instead of $199.00)
    const hideZeros = window.themeVariables?.settings?.currencyHideTrailingZeros && cents % 100 === 0;
    const value = hideZeros ? String(cents / 100) : (cents / 100).toFixed(2);
    const formatString = this.moneyFormat;

    if (formatString.includes('{{amount_with_comma_separator}}')) {
      return formatString.replace('{{amount_with_comma_separator}}', value.replace('.', ','));
    }
    if (formatString.includes('{{amount_no_decimals}}')) {
      return formatString.replace('{{amount_no_decimals}}', Math.round(cents / 100).toString());
    }
    if (formatString.includes('{{amount_with_apostrophe_separator}}')) {
      const parts = value.split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, "'");
      return formatString.replace('{{amount_with_apostrophe_separator}}', parts.join('.'));
    }
    if (formatString.includes('{{amount}}')) {
      return formatString.replace('{{amount}}', value);
    }

    return value;
  }

  disconnectedCallback() {
    // Fix #1: Proper cleanup - remove from listeners Set
    if (this.bundleState && this.bundleState.listeners) {
      this.bundleState.listeners.delete(this);
    }
  }
}

// Register the custom element
if (!customElements.get('build-your-bundle-mobile')) {
  customElements.define('build-your-bundle-mobile', BuildYourBundleMobile);
}
